import { createContext, useContext, useEffect, useId } from 'react';

import { replaceText } from '../../modules/utils/promotionHelper';

export interface WalletWidgetContainerContextValue {
  publishedText?: string;
  publishText: (publisherId: string, text: string | undefined) => void;
}

export const WalletWidgetContainerContext = createContext<WalletWidgetContainerContextValue | undefined>(undefined);

/**
 * Hook for child components to publish resolved badge text to the parent
 * WalletWidgetContainer. The container displays the last published value in
 * the header badge. Pass `undefined` to clear the published text.
 */
export const useWalletWidgetBadgePublisher = (text: string | undefined): void => {
  const ctx = useContext(WalletWidgetContainerContext);
  const publisherId = useId();

  useEffect(() => {
    if (!ctx) return undefined;
    ctx.publishText(publisherId, text);
    return () => ctx.publishText(publisherId, undefined);
  }, [ctx, publisherId, text]);
};

export const buildContainerCountBadgeText = (
  count: number,
  templates?: {
    defaultLabel?: string;
    availableSingularLabel?: string;
    availablePluralLabel?: string;
    zeroAvailableLabel?: string;
  }
): string => {
  const tokens = [String(count)];
  if (count === 0) {
    if (templates?.defaultLabel) return templates.defaultLabel;
    if (templates?.zeroAvailableLabel) return templates.zeroAvailableLabel;
    if (templates?.availablePluralLabel) return replaceText(templates.availablePluralLabel, tokens);
    return '';
  }
  if (count === 1) {
    if (templates?.availableSingularLabel) return replaceText(templates.availableSingularLabel, tokens);
    if (templates?.availablePluralLabel) return replaceText(templates.availablePluralLabel, tokens);
    return '';
  }
  return templates?.availablePluralLabel ? replaceText(templates.availablePluralLabel, tokens) : '';
};
